/**
 * Экран выбора/создания игры ведущим (до старта сессии).
 *
 * - «Открытая витрина» (category='showcase') — тематические готовые квизы
 *   (Космос/Звёздные войны/Наука); «Библиотека вопросов» (category='questions') —
 *   5 шаблонов по типам вопросов. Обе секции — общий контент (type='library'),
 *   грузятся через LibraryCatalog. Кнопка «Провести» → onChooseGame(id).
 * - Форма создания новой игры: название + JSON квиза (QuizPayload). «Создать» →
 *   POST /api/games → onChooseGame(newId). Есть кнопка «Загрузить пример».
 *
 * Личные пользовательские квизы (type='quiz') здесь НЕ листаются (приватность И4) —
 * они видны только владельцу в кабинете /me (owner-scoped /api/me/games).
 *
 * Сам запуск сессии (POST /api/sessions) делает родитель HostScreen по onChooseGame.
 *
 * i18n: переводится UI-обвязка. DEMO_QUIZ — это контент демо-викторины (вопросы/
 * варианты), он НЕ переводится (см. план И2: «демо-квиз HostSelect.tsx» остаётся).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createGame, type QuizPayload } from '../api';
import { LibraryCatalog } from '../components/LibraryCatalog';
import { errorMessage } from '../util';

/** Встроенный демо-квиз для быстрого старта без ручного JSON (контент — НЕ переводится). */
const DEMO_QUIZ: QuizPayload = {
  questions: [
    {
      title: 'Столица Франции?',
      options: ['Берлин', 'Париж', 'Мадрид', 'Рим'],
      correctIndex: 1,
      durationMs: 20000,
    },
    {
      title: 'Сколько будет 2 + 2 × 2?',
      options: ['6', '8', '4', '10'],
      correctIndex: 0,
      durationMs: 15000,
    },
    {
      title: 'Какая планета считается красной?',
      options: ['Венера', 'Юпитер', 'Марс', 'Сатурн'],
      correctIndex: 2,
      durationMs: 20000,
    },
  ],
};

export interface HostSelectProps {
  onChooseGame: (gameId: number) => void;
  onExit: () => void;
  /** Переход в редактор квизов (/edit). Если не задан — кнопка скрыта. */
  onOpenEditor?: () => void;
  /** Родитель стартует сессию — показать оверлей загрузки. */
  starting?: boolean;
  /** Ошибка старта сессии от родителя. */
  error?: string | null;
}

export function HostSelect({
  onChooseGame,
  onExit,
  onOpenEditor,
  starting = false,
  error = null,
}: HostSelectProps): JSX.Element {
  const { t } = useTranslation();

  const [title, setTitle] = useState(t('host.select.demoTitle'));
  const [payloadText, setPayloadText] = useState<string>(JSON.stringify(DEMO_QUIZ, null, 2));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  async function handleCreate(): Promise<void> {
    setCreateError(null);
    let quiz: QuizPayload;
    try {
      quiz = JSON.parse(payloadText) as QuizPayload;
    } catch {
      setCreateError(t('errors.invalidJson'));
      return;
    }
    if (!title.trim()) {
      setCreateError(t('errors.titleRequired'));
      return;
    }
    setCreating(true);
    try {
      const game = await createGame({ title: title.trim(), quiz });
      onChooseGame(game.id);
    } catch (e) {
      setCreateError(errorMessage(e));
    } finally {
      setCreating(false);
    }
  }

  return (
    <main className="card card--wide" aria-labelledby="host-title">
      <header className="screen-head">
        <h2 id="host-title" className="block-title">
          {t('host.select.title')}
        </h2>
        <div className="screen-head-meta">
          {onOpenEditor ? (
            <button type="button" className="btn btn--ghost" onClick={onOpenEditor}>
              {t('host.editorLabel')}
            </button>
          ) : null}
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            {t('nav.toMain')}
          </button>
        </div>
      </header>

      <section className="block">
        <LibraryCatalog
          category="showcase"
          title={t('host.select.showcaseTitle')}
          testId="library-showcase"
          onStart={(gameId) => onChooseGame(gameId)}
          onCloned={() => onOpenEditor?.()}
          starting={starting}
        />
      </section>

      <section className="block">
        <LibraryCatalog
          category="questions"
          title={t('host.select.questionsTitle')}
          testId="library-questions"
          onStart={(gameId) => onChooseGame(gameId)}
          onCloned={() => onOpenEditor?.()}
          starting={starting}
        />
      </section>

      <section className="block">
        <h3 className="block-subtitle">{t('host.select.createTitle')}</h3>
        <div className="form-row">
          <label className="field-label" htmlFor="game-title">
            {t('host.select.titleLabel')}
          </label>
          <input
            id="game-title"
            className="text-input"
            type="text"
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="form-row">
          <label className="field-label" htmlFor="game-payload">
            {t('host.select.payloadLabel')}
          </label>
          <textarea
            id="game-payload"
            className="textarea"
            rows={8}
            spellCheck={false}
            value={payloadText}
            onChange={(e) => setPayloadText(e.target.value)}
          />
        </div>
        <div className="btn-row">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setPayloadText(JSON.stringify(DEMO_QUIZ, null, 2))}
          >
            {t('host.select.loadExample')}
          </button>
          <button
            type="button"
            className="cta"
            disabled={creating || starting}
            onClick={() => void handleCreate()}
          >
            {creating ? t('common.creating') : t('host.select.createAndPlay')}
          </button>
        </div>
        {createError ? (
          <p className="error" role="alert">
            {createError}
          </p>
        ) : null}
      </section>

      {starting ? <p className="muted">{t('host.select.startingSession')}</p> : null}
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </main>
  );
}
